import { useEffect, useState } from "react";
import { Crown, Check } from "lucide-react";
import { api, formatAmount } from "@/lib/api";

const TierCard = ({ t, active, reached }) => (
  <div
    className={`panel p-5 flex flex-col gap-3 relative ${active ? "ring-1" : ""}`}
    style={{ "--tw-ring-color": t.color, opacity: reached ? 1 : 0.55, transition: "opacity 200ms ease" }}
    data-testid={`vip-tier-${t.name.toLowerCase()}`}
  >
    <div className="flex items-center gap-2">
      <Crown size={16} style={{ color: t.color }} />
      <span className="font-display font-black tracking-tighter" style={{ color: t.color }}>{t.name}</span>
      {active && <span className="chip ml-auto text-[10px]">Current</span>}
      {reached && !active && <Check size={14} className="ml-auto text-emerald-400" />}
    </div>
    <div className="font-mono-num text-2xl font-bold">{Math.round(t.rakeback * 100)}%</div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">rakeback</div>
    <div className="text-xs text-zinc-500 font-mono-num">from {formatAmount(t.min_wagered, "FMI")} FMI wagered</div>
  </div>
);

const Stat = ({ label, value, testid, accent }) => (
  <div className="panel p-5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="font-mono-num text-2xl font-bold mt-2" style={{ color: accent }}>{value}</div>
  </div>
);

export default function Vip() {
  const [me, setMe] = useState(null);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/vip/me").then((r) => setMe(r.data));
    api.get("/vip/tiers").then((r) => setTiers(r.data));
  }, []);

  if (!me) return <div className="p-10 text-zinc-500">Loading…</div>;
  const { tier, next } = me;

  return (
    <div className="flex flex-col gap-8 fade-up" data-testid="vip-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">VIP Club</h1>
        <p className="text-zinc-500 text-sm mt-1">Climb tiers by wagering FMI. Rakeback is paid every Monday 00:00 UTC.</p>
      </div>

      <section className="panel p-6 flex flex-col gap-4" data-testid="vip-progress">
        <div className="flex items-center gap-3">
          <Crown size={28} style={{ color: tier.color }} />
          <div>
            <div className="font-display text-2xl font-black tracking-tighter" style={{ color: tier.color }} data-testid="vip-current-tier">{tier.name}</div>
            <div className="text-xs text-zinc-500">{Math.round(tier.rakeback * 100)}% weekly rakeback</div>
          </div>
          {next && <div className="ml-auto text-xs text-zinc-500 font-mono-num" data-testid="vip-next-tier">Next: <span style={{ color: next.color }}>{next.name}</span> at {formatAmount(next.min_wagered, "FMI")}</div>}
        </div>
        <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${me.progress * 100}%`, background: `linear-gradient(90deg, ${tier.color}, ${next?.color || tier.color})`, transition: "width 600ms ease" }} data-testid="vip-progress-bar" />
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Lifetime FMI wagered" value={formatAmount(me.lifetime_wagered, "FMI")} testid="vip-lifetime" accent="#FFC107" />
        <Stat label="Wagered this week" value={formatAmount(me.week_wagered, "FMI")} testid="vip-week" accent="#00F0FF" />
        <Stat label="Pending rakeback" value={`${formatAmount(me.pending_rakeback, "FMI")} FMI`} testid="vip-pending" accent="#00E676" />
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {tiers.map((t, i) => <TierCard key={t.name} t={t} active={i === me.level} reached={i <= me.level} />)}
      </section>

      <section>
        <h2 className="font-display text-lg font-black tracking-tighter mb-3">Rakeback history</h2>
        <div className="panel overflow-hidden" data-testid="vip-history">
          {me.history.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">No payouts yet — your first lands next Monday.</div>
          ) : (
            me.history.map((h) => (
              <div key={h.id} className="flex items-center justify-between px-5 py-3 border-b border-white/[0.04] text-sm">
                <span className="text-zinc-400">{h.note}</span>
                <span className="font-mono-num text-emerald-400">+{formatAmount(h.amount, "FMI")} FMI</span>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
